import { SIGN_METHOD } from '@treyd-io/core/constants/auth';
import {
  BANKID_COUNTRIES,
  COUNTRY_SIGN_METHODS,
} from '@treyd-io/core/constants/country';

import { first, includes } from 'lodash';
import { assign, createMachine } from 'xstate';

export type Context = {
  country?: string;
  signingMethod: SIGN_METHOD;
};

export const createSigningFlowSM = ({ country }: { country?: string }) =>
  createMachine(
    {
      /** @xstate-layout N4IgpgJg5mDOIC5SwJZQHYvVABAMwBsB7AdwDoBBAVwBciBbAQxpQGMBZMGgCyIgBEuYViyLoAxK26NsYAMJEq6GgCcAngG0ADAF1EoAA5FUo9PpAAPRAFYAHAE4yAJgBs1lwHZbAFi1aPHgDM3gA0IGqItgCMZNZOflpR3vZ2Wt4OAL4ZYagYWLiEpJS0DMxsnDx8gjTCpmTVtfkAymiY2BW8EOLaekggRiYoYuZWCLYeYRFjPmSBHvaBLoFOyx4uaVk5rfn4xOTUdEwsHFydDSJD6PVCF9gtee2nfN1RvYbGKKYjiIFRTmTzFw+axrKJAlaBSaIJzWGL2IJRX5RDz+WxAjybEC5NoFPbFQ5lE6VAQ3OoAIxkAGsUF0pDIYPccR0+D1zANPpdvggovYYgk-E5vCs7K4oQgXDyyPDfstvHZfrDMdidoV9iUjuUniSahcxGQKehqRAyFgOYwCA0VPQsGUxN1dGyPl8+qNoh5nMFvOsXOlAtYtPYxS57N4yDz7MG-kivFEldtsLsigdSsdmdrGnqDUaTZgWObLdb0LaJBpXo7BsMXZFkR7vF6tD7bH6A2KvNYyG4nLYG2t7OMvXGHrik+rCWnznUiDQDJJpLJGfk06y+uznaBRjy+fzBcLbKLwogPFF2-ZXILop5vDzbFlsiB0Hw4OZlQnVeWOZX14gALQ8sXfk9u35YC-G8QccUTNUCVTLUJ05FcnXgr8ECcQMD25H0yG8BEhSSf1rCFG87xfYcoJTTViTgvU4LueMoDTd810sQ8JnQmEXA7QJ4W4lIVlsHxwJVPFkw1IkzlJS59SpGlGKQ5iEBBf57AbVDj3WWx3EhdDvQ7ewIwcIJrDmX5BNfYTRxgyiJMzaTjVNPMLSEK0bSY1c5NGFx4ilFTeXcLQNKWVsnBiaxT18qJomC7xAlM0j8XIsSqmsq4sxpMgAEcVFkz95KiLRfmceFEjWfikgWVtESlM90icHdapcWLIPi0Tx2SqTDTSgwwBUWAxHNAA5Kh6DJbrsrMKtuXymJUJRZEgWiZItKmbt2zmBYljWVTeUa1VmrHWC2qnAwxq5PKnCDdIASbLQVjBG7ap28zoIo8SdTqEiTomgIxQVMMuPmEMjOROJbwyIA */
      initial: 'AutomaticMethodDetection',
      id: 'signing flow',
      context: {
        country,
        signingMethod: first(COUNTRY_SIGN_METHODS[country || 'default']),
      } as Context,
      tsTypes: {} as import('./SigningFlowMachine.typegen').Typegen0,
      schema: {
        events: {} as
          | { type: 'changeSigningMethod'; signingMethod: SIGN_METHOD }
          | { type: 'changeCountry'; country: string },
      },
      states: {
        AutomaticMethodDetection: {
          id: 'AutomaticMethodDetection',
          initial: 'DetectingSigningMethod',

          on: {
            changeCountry: {
              actions: ['setCountry'],
            },
          },
          states: {
            DetectingSigningMethod: {
              always: [
                {
                  target: 'bankid',
                  cond: 'bankIdMethodDetected',
                },
                {
                  target: 'otp',
                },
              ],
            },
            bankid: {
              id: 'bankid',
              initial: 'initialDetermination',
              states: {
                initialDetermination: {
                  always: [
                    {
                      target: 'qr',
                      cond: 'qrMethodDetected',
                    },
                    {
                      target: 'personalNumber',
                    },
                  ],
                },
                qr: {
                  id: 'qr',
                  type: 'final',
                },
                personalNumber: {
                  id: 'personalNumber',
                  type: 'final',
                },
              },
              on: {
                changeSigningMethod: {
                  target: 'otp',
                  actions: ['setSigningMethod'],
                },
              },
            },
            otp: {
              on: {
                changeSigningMethod: {
                  target: 'bankid',
                  actions: ['setSigningMethod'],
                  cond: 'bankIdMethodDetected',
                },
              },
            },
            signing: {
              id: 'signing',
            },
          },
        },
      },
    },
    {
      actions: {
        setCountry: assign((_, event) => ({
          country: event.country,
        })),
        setSigningMethod: assign((_, event) => ({
          signingMethod: event.signingMethod,
        })),
      },
      guards: {
        bankIdMethodDetected: (context) =>
          includes(BANKID_COUNTRIES, context.country),
        qrMethodDetected: (context) => context.country === 'se',
      },
    }
  );
